import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CampaignSummary } from "@signalist/shared-types";

import { LoadingOverlay, Modal } from "@/components";
import { Table } from "@/components/Table/Table";
import { ArchivedView, ListOwnership, ListStatus, UpdateType } from "@/enum/list.enum";
import {
	useArchiveListMutation,
	useLazyGetLeanListsQuery,
	useUpdateListMutation,
} from "@/pages/Private/redux/list/list.api";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { OVERVIEW_REQUEST_SCOPE } from "@/pages/Private/redux/list/lean-lists.params";
import { useAppSelector } from "@/redux/hooks";
import { Order, SortParams } from "@/types/types";
import { translateRequestError } from "@/utils/errors";
import { useSnackBar } from "@/utils/useSnackbar";

import { reportActivationFailure } from "../../List/utils/listRefusalMessage";

import { archiveListsAndReport } from "../../List/utils/listLifecycleActions";

import { ALL_SOURCES } from "../../List/components/SourceTypePalette";
import { useListCampaignSummaries } from "../../List/components/ListCampaignSubRows";
import { useCampaignStatusPolling } from "../../List/hooks/useCampaignStatusPolling";
import { List, UpdateList } from "../../List/schema/list";
import { useCampaignBulkActions, SelectedCampaign } from "../hooks/useCampaignBulkActions";
import { CampaignsFiltersController } from "../hooks/useCampaignsFilters";
import {
	campaignSelectionKey,
	filterListCampaigns,
	matchesChannelFilter,
	matchesStatusFilter,
	shouldShowList,
} from "../utils/campaignFilters";
import { CampaignsBulkBar } from "./CampaignsBulkBar";
import { CampaignsFilterBar } from "./CampaignsFilterBar";
import { NewCampaignModal } from "./NewCampaignModal";
import { useCampaignsTableColumns } from "./useCampaignsTableColumns";

const PAGE_SIZE = 15;

interface CampaignsTableProps {
	/** Filter + pin-to-tab state, owned by `Campaigns.tsx` (see `useCampaignsFilters`). */
	filters: CampaignsFiltersController;
	/** Hands the parent a way to reset paging when a pinned tab restores a different view. */
	registerResetPaging?: (reset: () => void) => void;
	onPinCurrentView: (name: string) => void;
}

/**
 * The Campaigns overview table (PREC-3880): every list with campaigns (lean list rows, scoped by
 * the ownership tab) with its campaigns as auto-expanded sub-rows. Status/channel filters apply
 * client-side to the loaded rows; selection and the bulk bar act on campaigns, not lists.
 *
 * Filter state is owned by `useCampaignsFilters` one level up (see `Campaigns.tsx`) so that a
 * pinned tab can restore ownership and filters together; this component keeps only paging and
 * sorting, which no pinned tab captures.
 */
export const CampaignsTable: FunctionComponent<CampaignsTableProps> = ({
	filters,
	registerResetPaging,
	onPinCurrentView,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const {
		ownership,
		statusFilter,
		setStatusFilter,
		channelFilter,
		setChannelFilter,
		archivedView,
		setArchivedView,
		selectedSourceType,
		setSelectedSourceType,
		searchValue,
		setSearchValue,
		query,
		canPin,
		pinSummaryItems,
		suggestedPinName,
	} = filters;

	const [sorting, setSorting] = useState<SortParams>({ sortBy: "", sortOrder: Order.ASC });
	const [page, setPage] = useState(1);

	// `isError` follows the latest triggered args, so a failed "All" fetch is reported as such even
	// while the slice still holds an earlier tab's rows (PREC-4131).
	const [getLeanLists, { isLoading, isFetching, isError }] = useLazyGetLeanListsQuery();
	const { lists, listMeta } = useAppSelector(listSelector);

	const [updateList] = useUpdateListMutation();
	const [archiveList] = useArchiveListMutation();
	const [archiveTarget, setArchiveTarget] = useState<List | null>(null);
	const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);

	useCampaignStatusPolling();

	const { getSubRows, getSummaries, refresh, toggleCampaign } = useListCampaignSummaries(lists, {
		// A campaign status change that fails has no other way to reach the user (PREC-4129).
		onStatusChangeFailed: (count) =>
			handleOpenSnackbar(t("list.campaignBulk.statusChangeFailed", { count })),
	});

	// --- Campaign-level selection (the bulk bar acts on campaigns). ---
	const [selected, setSelected] = useState<Record<string, SelectedCampaign>>({});
	const selection = useMemo(() => Object.values(selected), [selected]);
	const clearSelection = useCallback(() => setSelected({}), []);

	const { duplicateSelected, isPending } = useCampaignBulkActions({
		refreshList: refresh,
		onDone: clearSelection,
	});

	useEffect(() => {
		registerResetPaging?.(() => setPage(1));
	}, [registerResetPaging]);

	useEffect(() => {
		setPage(1);
		clearSelection();
	}, [query, archivedView, selectedSourceType, ownership, sorting, clearSelection]);

	useEffect(() => {
		clearSelection();
	}, [statusFilter, channelFilter, clearSelection]);

	useEffect(() => {
		getLeanLists({
			scope: OVERVIEW_REQUEST_SCOPE,
			page,
			limit: PAGE_SIZE,
			searchValue: query,
			sourceType: selectedSourceType !== ALL_SOURCES ? selectedSourceType : undefined,
			// Unsorted state must omit the params entirely — the API's PageOptionsDTO rejects
			// sortBy="" with "sortBy must be a valid column identifier" (400).
			sorting: sorting.sortBy ? sorting : undefined,
			archived: archivedView === ArchivedView.HIDE ? false : undefined,
			hasCampaigns: true,
			ownership: ownership === ListOwnership.ALL ? undefined : ownership,
		});
	}, [getLeanLists, page, query, selectedSourceType, sorting, archivedView, ownership]);

	// Client-side campaign filters: a list stays visible while ≥1 campaign matches (or while its
	// summaries are still loading); its sub-rows are narrowed to the matching campaigns.
	const visibleLists = useMemo(
		() =>
			lists.filter((list) =>
				shouldShowList(getSummaries(list.id) ?? undefined, statusFilter, channelFilter)
			),
		[lists, getSummaries, statusFilter, channelFilter]
	);

	const filteredSummaries = useCallback(
		(listId: number): CampaignSummary[] =>
			filterListCampaigns(getSummaries(listId) ?? [], statusFilter, channelFilter),
		[getSummaries, statusFilter, channelFilter]
	);

	const getFilteredSubRows = useCallback(
		(listId: number) =>
			getSubRows(listId).filter(
				(row) =>
					"loading" in row ||
					(matchesStatusFilter(row, statusFilter) && matchesChannelFilter(row, channelFilter))
			),
		[getSubRows, statusFilter, channelFilter]
	);

	const isCampaignSelected = useCallback(
		(listId: number, campaignId: string) => !!selected[campaignSelectionKey(listId, campaignId)],
		[selected]
	);

	const toggleCampaignSelection = useCallback((listId: number, campaign: CampaignSummary) => {
		setSelected((prev) => {
			const key = campaignSelectionKey(listId, campaign.campaignId);
			const next = { ...prev };

			if (next[key]) {
				delete next[key];
			} else {
				next[key] = { listId, campaign };
			}

			return next;
		});
	}, []);

	const isListSelected = useCallback(
		(listId: number) => {
			const campaigns = filteredSummaries(listId);

			return (
				campaigns.length > 0 &&
				campaigns.every((campaign) => isCampaignSelected(listId, campaign.campaignId))
			);
		},
		[filteredSummaries, isCampaignSelected]
	);

	const toggleListSelection = useCallback(
		(list: List) => {
			const campaigns = filteredSummaries(list.id);
			const allSelected = isListSelected(list.id);

			setSelected((prev) => {
				const next = { ...prev };

				for (const campaign of campaigns) {
					const key = campaignSelectionKey(list.id, campaign.campaignId);

					if (allSelected) {
						delete next[key];
					} else {
						next[key] = { listId: list.id, campaign };
					}
				}

				return next;
			});
		},
		[filteredSummaries, isListSelected]
	);

	const selectAll = useMemo(
		() => visibleLists.length > 0 && visibleLists.every((list) => isListSelected(list.id)),
		[visibleLists, isListSelected]
	);

	const toggleSelectAll = useCallback(() => {
		if (selectAll) {
			clearSelection();

			return;
		}

		const next: Record<string, SelectedCampaign> = {};

		for (const list of visibleLists) {
			for (const campaign of filteredSummaries(list.id)) {
				next[campaignSelectionKey(list.id, campaign.campaignId)] = { listId: list.id, campaign };
			}
		}

		setSelected(next);
	}, [selectAll, clearSelection, visibleLists, filteredSummaries]);

	// --- Row handlers ---
	const handleToggleEnrichment = useCallback(
		async (id: number, status: ListStatus) => {
			const updater: Partial<UpdateList> = { id, updateType: UpdateType.STATUS };

			if (status === ListStatus.ACTIVE) {
				updater.status = ListStatus.PAUSED;
			} else if (status === ListStatus.PAUSED) {
				updater.status = ListStatus.ACTIVE;
			}

			try {
				await updateList({ ...updater }).unwrap();
			} catch (error) {
				reportActivationFailure(error, t("list.status.stoppingError"), handleOpenSnackbar);
			}
		},
		[updateList, handleOpenSnackbar, t]
	);

	const duplicateCampaign = useCallback(
		(listId: number, campaign: CampaignSummary) => duplicateSelected([{ listId, campaign }]),
		[duplicateSelected]
	);

	const confirmArchive = useCallback(async () => {
		if (!archiveTarget) {
			return;
		}

		try {
			const refused = await archiveListsAndReport(
				archiveList,
				[archiveTarget.id],
				!archiveTarget.archived,
				handleOpenSnackbar,
				[archiveTarget]
			);

			if (!refused) {
				setArchiveTarget(null);
			}
		} catch (err) {
			handleOpenSnackbar(translateRequestError(err));
		}
	}, [archiveList, archiveTarget, handleOpenSnackbar]);

	const columns = useCampaignsTableColumns({
		notify: handleOpenSnackbar,
		selectAll,
		toggleSelectAll,
		isListSelected,
		toggleListSelection,
		isCampaignSelected,
		toggleCampaignSelection,
		sortState: {
			sortBy: sorting.sortBy,
			sortOrder: sorting.sortBy ? (sorting.sortOrder === Order.DESC ? 2 : 1) : 0,
		},
		getSummaries,
		handleToggleEnrichment,
		toggleCampaign,
		duplicateCampaign,
		handleArchiveModal: setArchiveTarget,
		navigate,
	});

	if (isLoading) {
		return <LoadingOverlay />;
	}

	return (
		<div className="-mx-4 flex w-[calc(100%_+_2rem)] min-h-0 flex-1 flex-col">
			<CampaignsFilterBar
				archivedView={archivedView}
				channelFilter={channelFilter}
				searchValue={searchValue}
				selectedSourceType={selectedSourceType}
				statusFilter={statusFilter}
				onArchivedViewChange={setArchivedView}
				onChannelFilterChange={setChannelFilter}
				onNewCampaign={() => setShowNewCampaignModal(true)}
				onSearchChange={setSearchValue}
				onSourceTypeChange={setSelectedSourceType}
				onStatusFilterChange={setStatusFilter}
				pinDisabled={!canPin}
				pinSuggestedName={suggestedPinName}
				pinSummaryItems={pinSummaryItems}
				onPinCurrentView={onPinCurrentView}
			/>
			<div className="relative w-full">
				<Table
					canExpand={(row: List) => Number(row.campaignCount) > 0}
					columns={columns}
					data={isError ? [] : visibleLists}
					defaultExpanded
					getSubRows={(row: List) => getFilteredSubRows(row.id)}
					handleFetchMore={() => {
						if (
							listMeta?.itemCount &&
							lists.length < listMeta.itemCount &&
							!isFetching &&
							page < listMeta.pageCount
						) {
							setPage(page + 1);
						}
					}}
					handleSorting={(sortParams: SortParams) => {
						setSorting(sortParams);
					}}
					hideColumnsCount={true}
					hidePagination={true}
					isFetching={isFetching}
					isLoading={isLoading || (isFetching && page === 1)}
					lazyLoadPagination={true}
					noDataText={isError ? t("campaignsOverview.loadError") : t("campaignsOverview.empty")}
					overallCount={listMeta.itemCount}
					page={page}
					sticky={true}
					totalPages={listMeta.pageCount}
				/>
			</div>
			<CampaignsBulkBar
				isPending={isPending}
				selectedCount={selection.length}
				onClear={clearSelection}
				onDuplicate={() => duplicateSelected(selection)}
			/>
			<NewCampaignModal
				isOpen={showNewCampaignModal}
				onClose={() => setShowNewCampaignModal(false)}
			/>
			{archiveTarget && (
				<Modal
					handleClose={() => setArchiveTarget(null)}
					handleSave={confirmArchive}
					isOpened={!!archiveTarget}
					submitButtonText={
						archiveTarget.archived ? t("list.unarchiveList") : t("list.archiveList")
					}
					title={archiveTarget.archived ? t("list.unarchiveList") : t("list.archiveList")}
				>
					<div className="text-sm text-gray-700 mb-4">
						{t("campaignsOverview.archiveConfirmation", { name: archiveTarget.name })}
					</div>
				</Modal>
			)}
			{renderMessageSnackbar()}
		</div>
	);
};
